import type { KataScoringDefinition, ScoringCriterion } from './types'

function c(
  id: string,
  text: string,
  suggested: ScoringCriterion['suggested'],
  hint?: string,
): ScoringCriterion {
  return hint ? { id, text, suggested, hint } : { id, text, suggested }
}

const generalCeremony: ScoringCriterion[] = [
  c('nage-gen-start-pos', 'Tori decides the starting position', 'medium'),
  c(
    'nage-gen-engagement-pull',
    'Engagement distance: Uke starts with fundamental grip stepping forward; Tori reacts, pulls Uke forward to unbalance while stepping backward (Uki-otoshi, Kata-guruma, Harai-goshi, Tsurikomi-goshi, Sasae-tsurikomi-ashi, Yoko-gake). Engagement distance for Tomoe-nage: Tori and Uke in shizen-tai; Tori pushes Uke backward with Ayumi-ashi.',
    'medium',
  ),
  c(
    'nage-gen-tsugi-ashi',
    'Tsugi-ashi steps (Uki-otoshi, Kata-guruma, Harai-goshi, Tsurikomi-goshi, Sasae-tsurikomi-ashi, Yoko-gake)',
    'small_medium',
  ),
  c(
    'nage-gen-blow',
    'Tori and Uke in middle area, three steps distance (about 1.8 m), blow to the top of Tori’s head by Uke (Seoi-nage, Uki-goshi, Ura-nage, Yoko-guruma)',
    'small_medium',
    'Medium if Uke’s blow to the head is lateral',
  ),
  c(
    'nage-gen-okuri',
    'Tori and Uke in middle area, engagement distance; Uke grips, Tori pushes sideward; lateral steps (Okuri-ashi-harai)',
    'small_medium',
  ),
  c(
    'nage-gen-uchi-mata',
    'Tori and Uke in middle area, engagement distance; step forward to stand; Uke grips in shizentai (Uchi-mata)',
    'small_medium',
    'Backward step is a medium mistake',
  ),
  c(
    'nage-gen-tomoe-pos',
    'Tori and Uke on Uke’s side about 1 m from center; engagement distance; step forward to stand; grip in shizentai (Tomoe-nage)',
    'small_medium',
    'Backward step is a medium mistake',
  ),
  c(
    'nage-gen-jigotai',
    'Tori and Uke one step distance, migi/hidari jigotai; grip (Sumi-gaeshi, Uki-waza)',
    'small_medium',
  ),
  c(
    'nage-gen-direction-fall',
    'Technique performance direction and fall (Tomoe-nage / Sumi-gaeshi / Yoko-guruma / Uki-waza / Ura-nage / Yoko-gake as specified)',
    'small_medium',
  ),
]

/** Practice judging: 16 scored elements (max 160); opening/closing bows are catalog-only. */
export const nageNoKataScoring: KataScoringDefinition = {
  kataId: 'nage-no-kata',
  maxPerJudge: 160,
  maxPanelOfThree: 480,
  elements: [
    {
      id: 'nage-general',
      kind: 'ceremony',
      name: 'General elements',
      nameJa: '一般観察事項',
      criteria: generalCeremony,
    },
    {
      id: 'nage-uki-otoshi',
      kind: 'technique',
      name: 'Uki-otoshi',
      nameJa: '浮落',
      techniqueId: 'nage-uki-otoshi',
      groupId: 'te-waza',
      criteria: [
        c(
          'nage-uki-otoshi-1',
          'Uke takes grip, Tori does not raise his arms while stepping backward in tsugi-ashi; during the third step in a wide stride, Tori pulls down to throw Uke',
          'small',
        ),
        c('nage-uki-otoshi-2', 'When Uke is thrown, he doesn’t jump', 'medium'),
        c('nage-uki-otoshi-3', 'Tori finishes the technique looking forward', 'medium'),
        c('nage-uki-otoshi-4', 'Tori’s knee position (slightly inside)', 'small'),
      ],
    },
    {
      id: 'nage-seoi-nage',
      kind: 'technique',
      name: 'Seoi-nage',
      nameJa: '背負投',
      techniqueId: 'nage-seoi-nage',
      groupId: 'te-waza',
      criteria: [
        c('nage-seoi-nage-1', 'Uke attacks with two steps forward (left-right/ right-left foot)', 'medium'),
        c(
          'nage-seoi-nage-2',
          'Tori breaks Uke’s balance forward and loads him onto his right/ left shoulder to throw him',
          'medium',
        ),
        c(
          'nage-seoi-nage-3',
          'Uke’s defence: both feet aligned, heels lifted up due to Kuzushi (left/ right palm on Tori’s left/right hip)',
          'medium',
        ),
      ],
    },
    {
      id: 'nage-kata-guruma',
      kind: 'technique',
      name: 'Kata-guruma',
      nameJa: '肩車',
      techniqueId: 'nage-kata-guruma',
      groupId: 'te-waza',
      criteria: [
        c(
          'nage-kata-guruma-1',
          'Tori changes his grip on the sleeve during second step. Tori’s third step is bigger',
          'small_medium',
        ),
        c('nage-kata-guruma-2', 'Uke defends by stiffening his body', 'medium'),
        c(
          'nage-kata-guruma-3',
          'Tori moves into the lifting position (shifting his outside leg) without seizing Uke’s trouser',
          'small',
        ),
      ],
    },
    {
      id: 'nage-uki-goshi',
      kind: 'technique',
      name: 'Uki-goshi',
      nameJa: '浮腰',
      techniqueId: 'nage-uki-goshi',
      groupId: 'koshi-waza',
      criteria: [
        c('nage-uki-goshi-1', 'Uke attacks with two steps forward (left-right/ right-left foot)', 'medium'),
        c(
          'nage-uki-goshi-2',
          'Tori steps in to meet the attack and throws Uke diagonally (Uke no defence)',
          'small_medium',
        ),
      ],
    },
    {
      id: 'nage-harai-goshi',
      kind: 'technique',
      name: 'Harai-goshi',
      nameJa: '払腰',
      techniqueId: 'nage-harai-goshi',
      groupId: 'koshi-waza',
      criteria: [
        c(
          'nage-harai-goshi-1',
          'Tori changes his grip on lapel to Uke’s left/right shoulder blade through left/right armpit during the second step',
          'medium',
        ),
        c('nage-harai-goshi-2', 'Tori positions himself for tsukuri/kuzushi on the third step', 'medium'),
        c(
          'nage-harai-goshi-3',
          'Uke’s defence: both feet almost aligned, heels lifted up due to Kuzushi',
          'medium',
        ),
        c('nage-harai-goshi-4', 'Tori sweeps with an outstretched leg', 'small_medium'),
      ],
    },
    {
      id: 'nage-tsurikomi-goshi',
      kind: 'technique',
      name: 'Tsurikomi-goshi',
      nameJa: '釣込腰',
      techniqueId: 'nage-tsurikomi-goshi',
      groupId: 'koshi-waza',
      criteria: [
        c(
          'nage-tsurikomi-goshi-1',
          'On the third step Tori moves his right/left foot in front of Uke’s right/left foot to pull Uke forward',
          'small',
        ),
        c(
          'nage-tsurikomi-goshi-2',
          'Uke’s defence: both feet aligned leaning back his upper body without lifting heels.',
          'medium',
        ),
        c(
          'nage-tsurikomi-goshi-3',
          'Tori unbalances Uke forward immediately withdraws with left/right foot, pivots and passes very low with his hips pressed against Uke’s thighs, heels up only when he is unbalanced by Tori.',
          'small_medium',
        ),
      ],
    },
    {
      id: 'nage-okuri-ashi-harai',
      kind: 'technique',
      name: 'Okuri-ashi-harai',
      nameJa: '送足払',
      techniqueId: 'nage-okuri-ashi-harai',
      groupId: 'ashi-waza',
      criteria: [
        c(
          'nage-okuri-ashi-harai-1',
          'Tori must change the rhythm on the second and the third step and applies the kuzushi.',
          'small_medium',
        ),
        c(
          'nage-okuri-ashi-harai-2',
          'Tori sweeps horizontally and throws Uke in the direction he is moving without lifting Uke’s body.',
          'small_medium',
        ),
      ],
    },
    {
      id: 'nage-sasae-tsurikomi-ashi',
      kind: 'technique',
      name: 'Sasae-tsurikomi-ashi',
      nameJa: '支釣込足',
      techniqueId: 'nage-sasae-tsurikomi-ashi',
      groupId: 'ashi-waza',
      criteria: [
        c('nage-sasae-1', 'After second step, Tori has to move diagonally backward', 'medium'),
        c(
          'nage-sasae-2',
          'Tori throws Uke on his third step pivoting on the right/ left foot (he must not step forward)',
          'medium',
        ),
      ],
    },
    {
      id: 'nage-uchi-mata',
      kind: 'technique',
      name: 'Uchi-mata',
      nameJa: '内股',
      techniqueId: 'nage-uchi-mata',
      groupId: 'ashi-waza',
      criteria: [
        c('nage-uchi-mata-1', 'Tori turns Uke in wide circular movement (his arms pull horizontally)', 'medium'),
        c(
          'nage-uchi-mata-2',
          'During the third rotation, Tori sweeps Uke’s advancing leg up and outward.',
          'medium',
        ),
      ],
    },
    {
      id: 'nage-tomoe-nage',
      kind: 'technique',
      name: 'Tomoe-nage',
      nameJa: '巴投',
      techniqueId: 'nage-tomoe-nage',
      groupId: 'ma-sutemi-waza',
      criteria: [
        c('nage-tomoe-1', 'Tori moves forward with Ayumi-ashi breaking Uke’s balance backward', 'small'),
        c(
          'nage-tomoe-2',
          'Uke resists on the third step pushing back and stepping forward (both feet aligned)',
          'small',
        ),
        c(
          'nage-tomoe-3',
          'When Uke starts to push back Tori changes his grip, steps forward with his left/right foot inside Uke’s feet, lowering his body and lifting forward and upward to break Uke’s balance',
          'medium',
        ),
        c(
          'nage-tomoe-4',
          'Tori puts his right/left foot on Uke’s stomach at the same time Uke aligns his feet (Uke’s heels are up). Tori throws Uke over his head',
          'small',
        ),
      ],
    },
    {
      id: 'nage-ura-nage',
      kind: 'technique',
      name: 'Ura-nage',
      nameJa: '裏投',
      techniqueId: 'nage-ura-nage',
      groupId: 'ma-sutemi-waza',
      criteria: [
        c('nage-ura-1', 'Uke attacks with two steps forward (left-right/ right-left foot)', 'medium'),
        c(
          'nage-ura-2',
          'Tori avoids Uke’s blow during the second step, stepping forward very low, left-right/right-left foot (he must not jump with both feet at the same time), his left/right arm around Uke’s waist to draw Uke in and the palm of the right/left hand on his lower abdomen.',
          'medium',
        ),
        c(
          'nage-ura-3',
          'Tori throws Uke over his left/right shoulder and finishes with both shoulders on the mat',
          'medium',
        ),
      ],
    },
    {
      id: 'nage-sumi-gaeshi',
      kind: 'technique',
      name: 'Sumi-gaeshi',
      nameJa: '隅返',
      techniqueId: 'nage-sumi-gaeshi',
      groupId: 'ma-sutemi-waza',
      criteria: [
        c(
          'nage-sumi-1',
          'Tori unbalances Uke lifting his right/ left arm while stepping backwards in the jigo tai position',
          'small_medium',
        ),
        c(
          'nage-sumi-2',
          'Uke: to keep his balance, he brings his right/left foot forward parallel in a defensive position (jigotai)',
          'small_medium',
        ),
        c(
          'nage-sumi-3',
          'Tori, (right/left foot between Uke’s feet) breaks Uke’s balance forward again before throwing him over the head.',
          'medium',
        ),
      ],
    },
    {
      id: 'nage-yoko-gake',
      kind: 'technique',
      name: 'Yoko-gake',
      nameJa: '横掛',
      techniqueId: 'nage-yoko-gake',
      groupId: 'yoko-sutemi-waza',
      criteria: [
        c(
          'nage-yoko-gake-1',
          'Tori unbalances Uke tilting him over to his right/left front corner during the third step (half step) in order to throw him laterally',
          'medium',
        ),
        c(
          'nage-yoko-gake-2',
          'Tori steps backwards slowly with his right/left foot; as soon as it is next to the other foot, he immediately pushes Uke’s ankle with his left/ right foot, scooping up Uke\'s right/left sleeve with his left/right hand as he throws Uke’s body beside him.',
          'medium',
        ),
      ],
    },
    {
      id: 'nage-yoko-guruma',
      kind: 'technique',
      name: 'Yoko-guruma',
      nameJa: '横車',
      techniqueId: 'nage-yoko-guruma',
      groupId: 'yoko-sutemi-waza',
      criteria: [
        c('nage-yoko-guruma-1', 'Uke attacks with two steps forward (left-right/ right-left foot)', 'medium'),
        c('nage-yoko-guruma-2', 'Tori attempts to perform Ura-nage.', 'medium'),
        c('nage-yoko-guruma-3', 'Uke defends by bending forward.', 'medium'),
        c(
          'nage-yoko-guruma-4',
          'Tori breaks Uke’s balance forward and slides his right/left leg deeply between Uke’s legs.',
          'medium',
        ),
        c(
          'nage-yoko-guruma-5',
          'Tori lies down on his left /right side in order to throw Uke diagonally.',
          'medium',
        ),
      ],
    },
    {
      id: 'nage-uki-waza',
      kind: 'technique',
      name: 'Uki-waza',
      nameJa: '浮技',
      techniqueId: 'nage-uki-waza',
      groupId: 'yoko-sutemi-waza',
      criteria: [
        c(
          'nage-uki-waza-1',
          'Tori unbalances Uke lifting his right/left arm while stepping backwards in the jigotai position',
          'small_medium',
        ),
        c(
          'nage-uki-waza-2',
          'Uke to keep his balance, brings his right/left foot forward (jigotai).',
          'small_medium',
        ),
        c(
          'nage-uki-waza-3',
          'Tori breaks Uke’s balance diagonally forward and lies down on his side in order to throw Uke diagonally.',
          'medium',
        ),
      ],
    },
  ],
}
